import { ALL_DIRECTIONS, directionDelta } from "../direction";
import { isInLineOfSight } from "../boardUtil";
import type { DistanceMap } from "../distanceMap";
import { type RobotActor, RobotActorState } from "../interfaces";
import { breadthFirstSearch } from "../levelgenerator/levelGeneratorUtils";
import { MarkovChain } from "../markovChain";
import { PhysicalThingWithHp } from "../physicalThingWithHp";
import { randomChance, randomElement } from "../random";
import { serializeXY, deserializeXY } from "../serialization";
import { Mob } from "../mob";
import type { Sound } from "../things/sounds/sound";
import { WoodenDoor } from "../things/woodenDoor";
import { XY } from "../xy";
import { BasicAI } from "./basicAI";
import { BreakingTier, isBreakableObstacle } from "./breakingTier";
import { bumpDoorIfClosed, canMobStandIn, findSidestepTowards, isPositionInconvenient } from "./movement";

enum WanderingType {
	STOPPED = "STOPPED",
	RANDOM_WANDER = "RANDOM_WANDER",
	MOVING_TO_TARGET = "MOVING_TO_TARGET",
}

const wanderMarkov = new MarkovChain<WanderingType>();
wanderMarkov.addConfig(WanderingType.STOPPED, WanderingType.STOPPED, 95);
wanderMarkov.addConfig(WanderingType.STOPPED, WanderingType.RANDOM_WANDER, 5);
wanderMarkov.addConfig(WanderingType.RANDOM_WANDER, WanderingType.RANDOM_WANDER, 85);
wanderMarkov.addConfig(WanderingType.RANDOM_WANDER, WanderingType.STOPPED, 5);
wanderMarkov.addConfig(WanderingType.RANDOM_WANDER, WanderingType.MOVING_TO_TARGET, 10);
wanderMarkov.addConfig(WanderingType.MOVING_TO_TARGET, WanderingType.MOVING_TO_TARGET, 100);

export class RobotAI extends BasicAI {
	private static readonly ENGAGED_AGGRO_RADIUS_FACTOR = 2;
	private static readonly ENGAGED_AGGRO_REACHABLE_FACTOR = 1.7;

	private robotActor: RobotActor;
	private wanderingType: WanderingType = WanderingType.STOPPED;
	private currentWanderTarget: XY | null = null;

	powerOnSoundSupplier: (() => Sound) | null = null;
	targetAcquiredSoundSupplier: (() => Sound) | null = null;
	startAttackModeSoundSupplier: (() => Sound) | null = null;

	randomWanderMovementProbability = 0.3;
	movementVariationProbability = 0.3;
	pathfindCostSwitchToHeuristic = 999;
	breakingTier: BreakingTier = BreakingTier.NONE;

	constructor(actor: RobotActor) {
		super(actor);
		this.robotActor = actor;
	}

	processTurn(): void {
		// State transitions
		if (this.robotActor.getState() === RobotActorState.OFF) {
			if (this.shouldWakeUp()) this.wakeUp();
			return; // The activation turn is spent powering on; the robot acts from next turn.
		} else if (this.robotActor.getState() === RobotActorState.WANDERING) {
			if (this.shouldEngage()) this.engage();
		}

		// Attack mode transitions
		if (this.robotActor.getState() === RobotActorState.ENGAGING) {
			if (this.shouldActivateAttackMode()) this.startAttackMode();
		} else if (this.robotActor.getState() === RobotActorState.ATTACKING) {
			if (this.shouldDeactivateAttackMode()) this.stopAttackMode();
		}

		// Actions
		if (this.robotActor.getState() === RobotActorState.WANDERING) {
			this.wander();
		} else if (this.isEngaging()) {
			this.runBasicAI();
		}
	}

	private runBasicAI(): void {
		super.processTurn();
	}

	private shouldWakeUp(): boolean {
		const distanceMap = this.getDistanceMapToPlayer();
		if (!distanceMap) return false;
		const distance = distanceMap.get(this.actor.getXY());
		return distance !== undefined && distance <= 20;
	}

	private shouldEngage(): boolean {
		return this.shouldWakeUp();
	}

	private wakeUp(): void {
		this.robotActor.setState(RobotActorState.WANDERING);
		this.addSound(this.powerOnSoundSupplier);
	}

	private engage(): void {
		this.robotActor.setState(RobotActorState.ENGAGING);
		this.addSound(this.targetAcquiredSoundSupplier);
	}

	// Engaged robots see further.
	protected override getEffectiveAggroRadius(): number {
		const base = super.getEffectiveAggroRadius();
		return this.isEngaging() ? Math.floor(base * RobotAI.ENGAGED_AGGRO_RADIUS_FACTOR) : base;
	}

	protected override getEffectiveAggroReachable(): number {
		const base = super.getEffectiveAggroReachable();
		return this.isEngaging() ? Math.floor(base * RobotAI.ENGAGED_AGGRO_REACHABLE_FACTOR) : base;
	}

	private shouldActivateAttackMode(): boolean {
		const distanceMap = this.getDistanceMapToPlayer();
		if (!distanceMap) return false;
		const distance = distanceMap.get(this.actor.getXY());
		return distance !== undefined && distance <= 7;
	}

	private startAttackMode(): void {
		this.robotActor.setState(RobotActorState.ATTACKING);
		this.addSound(this.startAttackModeSoundSupplier);
	}

	private shouldDeactivateAttackMode(): boolean {
		const distanceMap = this.getDistanceMapToPlayer();
		if (!distanceMap) return true;
		const distance = distanceMap.get(this.actor.getXY());
		return distance === undefined || distance >= 10;
	}

	private stopAttackMode(): void {
		this.robotActor.setState(RobotActorState.ENGAGING);
	}

	private isEngaging(): boolean {
		return (
			this.robotActor.getState() === RobotActorState.ENGAGING ||
			this.robotActor.getState() === RobotActorState.ATTACKING
		);
	}

	private wander(): void {
		if (!this.actor.isMovementReady()) return;
		this.wanderingType = wanderMarkov.run(this.wanderingType);
		const board = this.getBoard();
		const inconvenient = isPositionInconvenient(board, this.actor.getXY());

		if (this.wanderingType === WanderingType.STOPPED && inconvenient) {
			this.wanderingType = WanderingType.RANDOM_WANDER;
		}

		if (this.wanderingType === WanderingType.STOPPED) return;

		if (this.wanderingType === WanderingType.RANDOM_WANDER) {
			if (!inconvenient && randomChance(1 - this.randomWanderMovementProbability)) return;
			const passable: XY[] = [];
			for (const dir of ALL_DIRECTIONS) {
				const proposedXY = this.actor.getXY().add(directionDelta(dir));
				if (canMobStandIn(board, proposedXY)) passable.push(proposedXY);
			}
			const dest = randomElement(passable);
			if (dest) this.moveActorTo(dest);
			return;
		}

		// MOVING_TO_TARGET
		if (this.currentWanderTarget === null) {
			this.currentWanderTarget = this.getWanderingTarget();
			if (this.currentWanderTarget === null) {
				this.wanderingType = WanderingType.RANDOM_WANDER;
				return;
			}
		}
		this.moveInStraightLineTo(this.currentWanderTarget);
		// moveInStraightLineTo nulls currentWanderTarget when it gives up on a fully blocked path.
		if (this.currentWanderTarget !== null && this.actor.getXY().equals(this.currentWanderTarget)) {
			this.wanderingType = WanderingType.STOPPED;
			this.currentWanderTarget = null;
		}
	}

	private moveInStraightLineTo(target: XY): void {
		const actorXY = this.actor.getXY();
		const dx = target.x - actorXY.x;
		const dy = target.y - actorXY.y;
		const stepX = Math.sign(dx);
		const stepY = Math.sign(dy);

		let nextStep = new XY(actorXY.x + stepX, actorXY.y + stepY);

		const board = this.getBoard();
		if (!canMobStandIn(board, nextStep)) {
			const sidestep = findSidestepTowards(actorXY, nextStep, target, (n) => canMobStandIn(board, n));
			if (sidestep === null) {
				this.wanderingType = WanderingType.STOPPED;
				this.currentWanderTarget = null;
				return;
			}
			nextStep = sidestep;
		}

		// Movement variation
		if (randomChance(this.movementVariationProbability)) {
			const nextStepNeighbors = nextStep.getNeighbors();
			const currentDist = nextStep.distanceTo(target);
			const alternatives = actorXY
				.getNeighbors()
				.filter((n) => nextStepNeighbors.some((ns) => ns.equals(n)))
				.filter((n) => canMobStandIn(board, n))
				.filter((n) => n.distanceTo(target) <= currentDist + 1);
			const alt = randomElement(alternatives);
			if (alt) nextStep = alt;
		}

		this.moveActorTo(nextStep);
	}

	private getWanderingTarget(): XY | null {
		const board = this.getBoard();
		const actorXY = this.actor.getXY();
		const accessible = breadthFirstSearch(actorXY, (xy) => board.isInBounds(xy) && board.isPassable(xy));
		const accessibleSet = new Set(accessible.map((xy) => xy.toString()));

		const frontier = new Set<string>();
		for (const a of accessible) {
			for (const n of a.getNeighbors()) {
				if (!board.isInBounds(n)) continue;
				if (accessibleSet.has(n.toString())) continue;
				frontier.add(n.toString());
			}
		}

		const doors: XY[] = [];
		for (const key of frontier) {
			const xy = XY.fromString(key);
			if (xy.equals(actorXY)) continue;
			if (board.getThingOfClass(WoodenDoor, xy) === null) continue;
			if (!isInLineOfSight(board, actorXY, xy)) continue;
			doors.push(xy);
		}

		return randomElement(doors) ?? null;
	}

	protected moveActorTo(xy: XY): void {
		bumpDoorIfClosed(this.getBoard(), xy, this.actor as any);
		super.moveActorTo(xy);
	}

	protected moveTowardsTarget(target: PhysicalThingWithHp): boolean {
		if (!this.actor.isMovementReady()) return false;

		const targetXY = target.xy;
		const actorXY = this.actor.getXY();

		// Line-of-sight: move in a straight line if path is clear
		if (isInLineOfSight(this.getBoard(), actorXY, targetXY)) {
			this.moveInStraightLineTo(targetXY);
			return true;
		}

		if (this.breakingTier === BreakingTier.NONE) {
			return this.followClearPath(target);
		}

		const board = this.getBoard();
		const clearMap = board.getDistanceMapToPlayerIncludingDoors();
		const digMap = board.getDistanceMapForBreakingTier(this.breakingTier);
		const clearDist = clearMap?.get(actorXY);
		const digDist = digMap?.get(actorXY);

		// Both unreachable: let BasicAI fall through to break + heuristic.
		if (clearDist === undefined && digDist === undefined) return false;

		const useDigPath =
			digDist !== undefined && (clearDist === undefined || clearDist > digDist * this.pathfindCostSwitchToHeuristic);

		if (useDigPath) return this.followDigPath(digMap!, target);
		return this.followClearPath(target);
	}

	private followClearPath(target: PhysicalThingWithHp): boolean {
		const board = this.getBoard();
		const actorXY = this.actor.getXY();
		const targetXY = target.xy;
		const distanceMap = board.getDistanceMapToPlayerIncludingDoors();
		if (!distanceMap) return false;

		const path = distanceMap.rollDownhill(actorXY);
		if (path.length === 0) return false;

		// Only enforce the heuristic-switch threshold for NONE-tier robots; tiered
		// robots already made that decision against the dig map in moveTowardsTarget.
		if (this.breakingTier === BreakingTier.NONE) {
			const pathfindDistance = path.length;
			const euclideanDistance = actorXY.distanceTo(targetXY);
			if (pathfindDistance > euclideanDistance * this.pathfindCostSwitchToHeuristic) return false;
		}

		let nextStep = path[0];
		if (!canMobStandIn(board, nextStep)) {
			const sidestep = findSidestepTowards(actorXY, nextStep, targetXY, (n) => canMobStandIn(board, n));
			if (sidestep === null) return false;
			nextStep = sidestep;
		}

		this.moveActorTo(nextStep);
		return true;
	}

	private followDigPath(digMap: DistanceMap, _target: PhysicalThingWithHp): boolean {
		const actorXY = this.actor.getXY();
		const path = digMap.rollDownhill(actorXY);
		if (path.length === 0) return false;
		const nextStep = path[0];
		const board = this.getBoard();

		if (canMobStandIn(board, nextStep)) {
			this.moveActorTo(nextStep);
			return true;
		}

		// Blocked tile that the dig map considers passable: attack the diggable thing on it.
		for (const thing of board.getThingsAt(nextStep)) {
			if (!(thing instanceof PhysicalThingWithHp)) continue;
			if (!this.isDamageableObstacle(thing)) continue;
			if (!this.actor.canAttack(thing)) continue;
			this.attack(thing);
			return true;
		}

		return false;
	}

	protected isDamageableObstacle(thing: PhysicalThingWithHp): boolean {
		return isBreakableObstacle(thing) || (thing instanceof Mob && thing.isFrozen());
	}

	protected idle(): void {
		this.robotActor.setState(RobotActorState.WANDERING);
		this.wanderingType = WanderingType.RANDOM_WANDER;
		this.wander();
	}

	override serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			wanderingType: this.wanderingType,
			currentWanderTarget: this.currentWanderTarget ? serializeXY(this.currentWanderTarget) : null,
		};
	}

	override restoreFrom(data: any): void {
		if (!data) return;
		super.restoreFrom(data);
		if (data.wanderingType) this.wanderingType = data.wanderingType;
		this.currentWanderTarget = data.currentWanderTarget ? deserializeXY(data.currentWanderTarget) : null;
	}
}
